import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useForm } from "../../../hooks/useForm";
import PageWrapper from "../../common/components/PageWrapper";
import OmniTable from "../../omni-common/components/OmniTable";
import PostSearchFilters from "../components/PostSearchFilters";
import PostTable, { Post } from "../components/PostTable";
import PostBulkActions from "../components/PostBulkActions";
import PostDetailPage from "../components/PostDetailPage";
import { useOmniPostsQuery, useOmniPostQuery, useOmniMarkMultiplePostsReadyMutation, useOmniDeletePostMutation, SearchFieldType, useOmniChannelsQuery } from "../../../lib/apollo/graphql/omni/omni-generated";

interface DateRange {
  startDate?: string;
  endDate?: string;
}

export default function OmniPostContainer() {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [filters, onChange] = useForm({
    page: 1,
    limit: 10,
    searchQuery: "",
    searchType: "ALL",
    statusFilter: "",
    channelFilter: "",
    dateTypeFilter: "CREATED",
    sortKey: "",
    sortDirection: "ASC" as "ASC" | "DESC",
  });

  const [dateRange, setDateRange] = useState<DateRange>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedDatePreset, setSelectedDatePreset] = useState<string>("ALL");

  // Mutations
  const [markPostsReady] = useOmniMarkMultiplePostsReadyMutation();
  const [deletePost] = useOmniDeletePostMutation();

  // GraphQL 쿼리 변수 구성
  const queryVariables = useMemo(() => {
    const input: any = {
      pagination: {
        page: filters.page,
        limit: filters.limit,
      },
    };

    if (filters.searchQuery.trim()) {
      input.search = {
        fields: filters.searchType === "ALL" ? [SearchFieldType.Title, SearchFieldType.Content] : [SearchFieldType.Title],
        keyword: filters.searchQuery,
      };
    }

    if (filters.statusFilter) {
      input.status = filters.statusFilter;
    }

    if (filters.channelFilter) {
      input.channelId = filters.channelFilter;
    }

    if (dateRange.startDate || dateRange.endDate) {
      // 한국 시간 기준 날짜를 UTC로 변환
      const convertedDateRange: { startDate?: string; endDate?: string } = {};

      if (dateRange.startDate) {
        // 선택한 날짜의 전날 15:00:00.000Z (한국 시간 당일 00:00:00)
        const [year, month, day] = dateRange.startDate.split('-');
        const selectedDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0));
        const prevDay = new Date(selectedDate.getTime() - (24 * 60 * 60 * 1000));
        convertedDateRange.startDate = `${prevDay.getUTCFullYear()}-${String(prevDay.getUTCMonth() + 1).padStart(2, '0')}-${String(prevDay.getUTCDate()).padStart(2, '0')}T15:00:00.000Z`;
      }

      if (dateRange.endDate) {
        // 선택한 날짜 14:59:59.999Z (한국 시간 당일 23:59:59.999)
        convertedDateRange.endDate = `${dateRange.endDate}T14:59:59.999Z`;
      }

      input.dateRange = convertedDateRange;
    }

    if (filters.sortKey) {
      input.sortBy = filters.sortKey;
      input.sortOrder = filters.sortDirection;
    }

    return { input };
  }, [filters, dateRange]);

  // GraphQL 쿼리 실행
  const { data, loading, refetch: refetchPostsList } = useOmniPostsQuery({
    variables: queryVariables,
    fetchPolicy: 'network-only', // 항상 서버에서 최신 데이터 가져오기
  });

  // 채널 목록 조회
  const { data: channelsData } = useOmniChannelsQuery({
    variables: {
      input: {
        page: 1,
        size: 100, // 모든 채널을 가져오기 위해 충분히 큰 값
      },
    },
  });

  const channels = useMemo(() => {
    return channelsData?.channels?.items || [];
  }, [channelsData]);

  // 응답 데이터를 Post 인터페이스에 맞게 변환
  const posts: Post[] = useMemo(() => {
    if (!data?.posts?.items) return [];

    console.log('=== 게시글 데이터 조회 ===');
    console.log('전체 게시글 수:', data.posts.items.length);
    console.log('게시글 데이터:', data.posts.items.map((item: any) => ({
      postId: item.postId,
      title: item.title,
      status: item.status,
      deletedAt: item.deletedAt,
    })));
    console.log('======================');

    return data.posts.items.map((item: any) => ({
      postId: item.postId,
      title: item.title,
      status: item.status,
      channelId: item.channelId,
      channel: item.channelRef?.name,
      draftId: item.draftId,
      planId: item.planId,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      updatedBy: item.updatedBy,
      publishedAt: item.publishedAt,
      readyMarkedAt: item.readyMarkedAt,
    }));
  }, [data]);

  const totalPages = data?.posts?.totalPages || 0;

  const handleSearch = () => {
    // 검색은 queryVariables 변경으로 자동 실행
  };

  const handleDatePresetSelect = (preset: string) => {
    setSelectedDatePreset(preset);
    // 한국 시간(KST, UTC+9) 기준으로 오늘 날짜 가져오기
    const getKSTDate = () => {
      const now = new Date();
      // UTC 시간에 9시간을 더해 한국 시간으로 변환
      const kstOffset = 9 * 60; // 9시간을 분으로 변환
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // UTC 시간
      return new Date(utc + (kstOffset * 60000)); // KST 시간
    };
    const today = getKSTDate();
    // 날짜를 YYYY-MM-DD 형식으로 변환 (화면 표시용)
    const getDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (preset) {
      case "ALL":
        setDateRange({});
        break;
      case "TODAY":
        setDateRange({ startDate: getDateString(today), endDate: getDateString(today) });
        break;
      case "3DAYS": {
        const d = new Date(today);
        d.setDate(today.getDate() - 3);
        setDateRange({ startDate: getDateString(d), endDate: getDateString(today) });
        break;
      }
      case "7DAYS": {
        const d = new Date(today);
        d.setDate(today.getDate() - 7);
        setDateRange({ startDate: getDateString(d), endDate: getDateString(today) });
        break;
      }
      case "1MONTH": {
        const d = new Date(today);
        d.setMonth(today.getMonth() - 1);
        setDateRange({ startDate: getDateString(d), endDate: getDateString(today) });
        break;
      }
      case "6MONTHS": {
        const d = new Date(today);
        d.setMonth(today.getMonth() - 6);
        setDateRange({ startDate: getDateString(d), endDate: getDateString(today) });
        break;
      }
      case "1YEAR": {
        const d = new Date(today);
        d.setFullYear(today.getFullYear() - 1);
        setDateRange({ startDate: getDateString(d), endDate: getDateString(today) });
        break;
      }
      case "CUSTOM":
        break;
    }
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedIds(selected ? posts.map(p => p.postId) : []);
  };

  const handleSelectPost = (id: string, selected: boolean) => {
    setSelectedIds(prev => (selected ? [...prev, id] : prev.filter(x => x !== id)));
  };

  const handleMarkReady = async () => {
    if (selectedIds.length === 0) return;

    if (window.confirm(`${selectedIds.length}개의 게시글을 게시예정으로 처리하시겠습니까?`)) {
      try {
        await markPostsReady({
          variables: {
            input: {
              postIds: selectedIds,
            },
          },
        });
        alert("게시예정으로 처리되었습니다.");
        setSelectedIds([]);
        refetchPostsList();
      } catch (error) {
        console.error('Failed to mark posts as ready:', error);
        const errorMessage = error instanceof Error ? error.message : '게시예정 처리에 실패했습니다.';
        alert(`처리 실패: ${errorMessage}`);
      }
    }
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;

    if (window.confirm(`${selectedIds.length}개의 게시글을 삭제하시겠습니까?`)) {
      try {
        console.log('=== 게시글 삭제 시작 ===');
        console.log('삭제할 게시글 IDs:', selectedIds);

        // 각 게시글에 대해 삭제 mutation 실행
        for (const postId of selectedIds) {
          console.log(`게시글 ${postId} 삭제 요청...`);
          const result = await deletePost({
            variables: {
              input: {
                postId: postId,
              },
            },
          });
          console.log(`게시글 ${postId} 삭제 응답:`, result);
        }

        console.log('=== 게시글 삭제 완료 ===');
        alert("삭제되었습니다.");
        setSelectedIds([]);
        refetchPostsList();
      } catch (error) {
        console.error('=== 게시글 삭제 에러 ===');
        console.error('Error:', error);
        if (error instanceof Error) {
          console.error('Error message:', error.message);
          console.error('Error stack:', error.stack);
        }
        console.error('Selected post IDs:', selectedIds);
        console.error('======================');

        const errorMessage = error instanceof Error ? error.message : '삭제에 실패했습니다.';
        alert(`삭제 실패: ${errorMessage}`);
      }
    }
  };

  const handlePostClick = (post: Post) => {
    navigate(`/omni/post/${post.postId}`);
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    onChange("sortKey", key);
    onChange("sortDirection", direction);
  };

  const handleReset = () => {
    onChange("searchQuery", "");
    onChange("searchType", "ALL");
    onChange("statusFilter", "");
    onChange("channelFilter", "");
    onChange("dateTypeFilter", "CREATED");
    onChange("sortKey", "");
    onChange("sortDirection", "ASC");
    onChange("page", 1);

    setDateRange({});
    setSelectedDatePreset("ALL");
    setSelectedIds([]);
  };

  const handleDetailSave = (_updatedPost: Post) => {
    navigate("/omni/post");
  };

  const handleDetailDelete = (_postId: string) => {
    // 목록 새로고침 후 목록으로 이동
    refetchPostsList();
    navigate("/omni/post");
  };

  const handleDetailPageClose = () => {
    navigate("/omni/post");
  };

  // 개별 post detail 쿼리
  const {
    data: postDetailData,
    loading: postDetailLoading,
    error: postDetailError,
    refetch: refetchPostDetail,
  } = useOmniPostQuery({
    variables: {
      postId: postId || "",
    },
    skip: !postId, // postId가 없으면 쿼리 스킵
    fetchPolicy: 'network-only', // 항상 서버에서 최신 데이터 가져오기
  });

  // URL 파라미터로 상세 페이지 판단
  if (postId) {
    if (postDetailLoading) {
      return (
        <PageWrapper className="!p-0 !bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">로딩 중...</div>
          </div>
        </PageWrapper>
      );
    }

    if (postDetailError || !postDetailData?.post) {
      navigate("/omni/post");
      return null;
    }

    const selectedPost: Post = {
      postId: postDetailData.post.postId,
      title: postDetailData.post.title,
      status: postDetailData.post.status,
      channelId: postDetailData.post.channelId,
      channel: (postDetailData.post as any).channelRef?.name,
      draftId: postDetailData.post.draftId,
      planId: "", // 상세에는 없을 수 있음
      createdAt: postDetailData.post.createdAt,
      updatedAt: postDetailData.post.updatedAt,
      updatedBy: postDetailData.post.updatedBy ?? undefined,
      content: postDetailData.post.content,
      memo: (postDetailData.post as any).memo,
    };

    // 상세 페이지 레이아웃(원고 상세와 동일)
    return (
      <PageWrapper className="!p-0 !bg-white">
        <div className="flex flex-col h-[100dvh]">
          <div className="border-b bg-white px-6 py-4 flex-shrink-0">
            <div className="flex items-center gap-4">
              <button
                onClick={handleDetailPageClose}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold">게시글 상세</h1>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <PostDetailPage
              post={selectedPost}
              onClose={handleDetailPageClose}
              onSave={handleDetailSave}
              onDelete={handleDetailDelete}
              onRefresh={async () => {
                await refetchPostDetail();
                await refetchPostsList();
              }}
            />
          </div>
        </div>
      </PageWrapper>
    );
  }

  // 목록 페이지 레이아웃
  return (
    <div className="w-full min-w-[1200px] max-w-full bg-zinc-100 p-3 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-2xl font-bold">게시글관리</h1>
      </div>

      <div className="mb-4 flex-shrink-0">
        <PostSearchFilters
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => onChange("searchQuery", value)}
          onSearch={handleSearch}
          searchType={filters.searchType}
          onSearchTypeChange={(value) => onChange("searchType", value)}
          statusFilter={filters.statusFilter}
          onStatusChange={(value) => onChange("statusFilter", value)}
          channelFilter={filters.channelFilter}
          onChannelChange={(value) => onChange("channelFilter", value)}
          dateTypeFilter={filters.dateTypeFilter}
          onDateTypeChange={(value) => onChange("dateTypeFilter", value)}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onDatePresetSelect={handleDatePresetSelect}
          selectedDatePreset={selectedDatePreset}
          onReset={handleReset}
          channels={channels}
        />
      </div>

      <div className="flex-1 min-h-0 mb-4">
        <PostTable
          posts={posts}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectPost={handleSelectPost}
          onPostClick={handlePostClick}
          isLoading={loading}
          sortKey={filters.sortKey}
          sortDirection={filters.sortDirection}
          onSort={handleSort}
        />
      </div>

      <div className="flex flex-col gap-3 flex-shrink-0" style={{ minHeight: selectedIds.length > 0 ? '80px' : '50px' }}>
        <OmniTable.Pagination
          total={totalPages}
          page={filters.page}
          onPageChange={(page) => onChange("page", page)}
        />

        <PostBulkActions
          selectedCount={selectedIds.length}
          onMarkReady={handleMarkReady}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}
